import React from 'react';
import { toTitleCase } from '@/utils/to-title-case';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Unstable_Grid2 as Grid,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Buildings as BuildingsIcon,
  CalendarX as CalendarIcon,
  Clock as ClockIcon,
  Envelope as EnvelopeIcon,
  Eye as EyeIcon,
  House as HouseIcon,
  MapPin as MapPinIcon,
  Notepad as NotepadIcon,
  Phone as PhoneIcon,
  User as UserIcon,
  Users as UsersIcon,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import type { Request } from '@/types/v2/request';
import { Request_Status } from '@/types/v2/request';
import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import { RouterLink } from '@/components/core/v2/link';

interface RequestDetailsCardProps {
  request: Request;
  isLoading: boolean;
  error: Error | null;
}

export function RequestDetailsCard({ request, isLoading, error }: RequestDetailsCardProps): React.JSX.Element {
  const { user, account, community } = useUser();
  const theme = useTheme();
  const navigate = useNavigate();

  // Helper functions to check user permissions
  const hasUserRole = (roles: string[]): boolean => {
    if (!user?.roles) return false;
    return user.roles.some((role) => roles.includes(role.name));
  };

  const hasAccountRole = (roles: string[]): boolean => {
    if (!account?.roles) return false;
    return account.roles.some((role) => roles.includes(role));
  };

  const hasCommunityRole = (roles: string[]): boolean => {
    if (!community?.roles) return false;
    return community.roles.some((role) => roles.includes(role));
  };

  // Check if user can view user details (admin, account admin, community manager)
  const canViewUserDetails = (): boolean => {
    return (
      hasUserRole(['super_admin', 'platform_admin', 'developer']) ||
      hasAccountRole(['account_owner', 'account_admin']) ||
      hasCommunityRole(['community_manager'])
    );
  };

  // Check if user can view detailed unit information
  const canViewUnitDetails = (): boolean => {
    return (
      hasUserRole(['super_admin', 'platform_admin', 'developer']) ||
      hasAccountRole(['account_owner', 'account_admin']) ||
      hasCommunityRole(['community_manager'])
    );
  };

  // Check if user can view all request details (for community members, show limited info)
  const canViewAllDetails = (): boolean => {
    return (
      hasUserRole(['super_admin', 'platform_admin', 'developer']) ||
      hasAccountRole(['account_owner', 'account_admin']) ||
      hasCommunityRole(['community_manager'])
    );
  };

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusName = (statusId: number) => {
    return Request_Status[statusId as keyof typeof Request_Status] || 'Unknown';
  };

  const formatUserList = (users: any[], role: string) => {
    if (!users || users.length === 0) {
      return `No ${role}`;
    }

    // If user can't view user details, just show names without links
    if (!canViewUserDetails()) {
      return users.map((user) => `${user.first_name} ${user.last_name}`).join(', ');
    }

    // If user can view details, show clickable links
    return users.map((user, index) => (
      <React.Fragment key={user.id}>
        <Link
          onClick={() => { navigate(`${paths.users}/${user.id}`); }}
          sx={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
        >
          {`${user.first_name} ${user.last_name}`}
        </Link>
        {index < users.length - 1 && ', '}
      </React.Fragment>
    ));
  };

  return (
    <Stack spacing={3}>
      {/* Main Request Details Card */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: theme.shadows[2],
          border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardHeader
          avatar={
            <Avatar
              sx={{
                backgroundColor: 'primary.main',
                width: 48,
                height: 48,
              }}
            >
              <NotepadIcon size={24} />
            </Avatar>
          }
          subheader={
            <Typography color="text.secondary" variant="body2">
              {!request?.unit_id ? 'Community Request' : 'Unit-specific Request'}
            </Typography>
          }
          title={
            <Typography sx={{ fontWeight: 600 }} variant="h6">
              Request Details
            </Typography>
          }
        />

        <CardContent sx={{ pt: 0 }}>
          <Stack spacing={3}>
            {/* Summary & Description */}
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }} variant="subtitle2">
                Summary
              </Typography>
              <Typography sx={{ mb: 2, lineHeight: 1.6 }} variant="body1">
                {request?.summary || 'No summary provided'}
              </Typography>

              {request?.description ? <>
                  <Typography sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }} variant="subtitle2">
                    Description
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      p: 2,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                      borderRadius: 2,
                      border:
                        theme.palette.mode === 'dark'
                          ? '1px solid rgba(255, 255, 255, 0.1)'
                          : '1px solid rgba(0, 0, 0, 0.1)',
                    }}
                    variant="body2"
                  >
                    {request.description}
                  </Typography>
                </> : null}
            </Box>

            {/* Key Information Grid */}
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }} variant="subtitle2">
                Request Information
              </Typography>
              <Grid container spacing={2}>
                {/* Created Date */}
                <Grid sm={6} xs={12}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: 'rgba(99, 102, 241, 0.02)',
                      border: '1px solid rgba(99, 102, 241, 0.1)',
                    }}
                  >
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                      <CalendarIcon color={theme.palette.primary.main} size={20} />
                      <Box>
                        <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="caption">
                          Created
                        </Typography>
                        <Typography sx={{ fontWeight: 600 }} variant="body2">
                          {request?.created_at ? formatDate(request.created_at) : 'Unknown'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Incident Date */}
                {request?.incident_date ? <Grid sm={6} xs={12}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(245, 158, 11, 0.02)',
                        border: '1px solid rgba(245, 158, 11, 0.1)',
                      }}
                    >
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <ClockIcon color={theme.palette.warning.main} size={20} />
                        <Box>
                          <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="caption">
                            Incident Date
                          </Typography>
                          <Typography sx={{ fontWeight: 600 }} variant="body2">
                            {formatDate(request.incident_date)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid> : null}

                {/* Created By */}
                {request?.created_by ? <Grid sm={6} xs={12}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(34, 197, 94, 0.02)',
                        border: '1px solid rgba(34, 197, 94, 0.1)',
                      }}
                    >
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <UserIcon color={theme.palette.success.main} size={20} />
                        <Box>
                          <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="caption">
                            Created By
                          </Typography>
                          <Typography sx={{ fontWeight: 600 }} variant="body2">
                            {request.created_by.first_name} {request.created_by.last_name}
                          </Typography>
                          {canViewUserDetails() && (
                            <Button
                              component={RouterLink}
                              href={`${paths.users.index}/${request.created_by.id}`}
                              size="small"
                              startIcon={<EyeIcon size={14} />}
                              sx={{
                                mt: 0.5,
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                minHeight: 'auto',
                                p: 0.5,
                              }}
                            >
                              View Profile
                            </Button>
                          )}
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid> : null}
              </Grid>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Unit Information Card */}
      {request?.unit_id && request?.unit && canViewUnitDetails() ? <Card
          sx={{
            borderRadius: 2,
            boxShadow: theme.shadows[1],
            border:
              theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
          }}
        >
          <CardHeader
            action={
              <Button
                component={RouterLink}
                href={paths.units.details(request.unit.id.toString())}
                size="small"
                startIcon={<EyeIcon size={16} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 1.5,
                  px: 2,
                }}
                variant="outlined"
              >
                View Unit
              </Button>
            }
            avatar={
              <Avatar
                sx={{
                  backgroundColor: 'primary.main',
                  width: 40,
                  height: 40,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <HouseIcon size={20} />
              </Avatar>
            }
            subtitle={
              <Typography color="text.secondary" variant="body2">
                Property details and location
              </Typography>
            }
            title={
              <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="h6">
                Unit Information
              </Typography>
            }
          />

          <CardContent sx={{ pt: 0, pb: 3 }}>
            <Grid container spacing={3}>
              {/* Unit Address - Enhanced */}
              <Grid md={8} xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 1.5,
                    backgroundColor:
                      theme.palette.mode === 'dark' ? 'rgba(6, 182, 212, 0.08)' : 'rgba(6, 182, 212, 0.04)',
                    border:
                      theme.palette.mode === 'dark'
                        ? '1px solid rgba(6, 182, 212, 0.2)'
                        : '1px solid rgba(6, 182, 212, 0.15)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark' ? 'rgba(6, 182, 212, 0.12)' : 'rgba(6, 182, 212, 0.06)',
                      transform: 'translateY(-1px)',
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  <Stack direction="row" spacing={2.5} sx={{ alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: theme.palette.info.main,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                      }}
                    >
                      <MapPinIcon size={20} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          mb: 1.5,
                          display: 'block',
                        }}
                        variant="caption"
                      >
                        Address
                      </Typography>
                      {request.unit.unit_number ? <Typography
                          sx={{ fontWeight: 600, mb: 1, color: 'text.primary', lineHeight: 1.3 }}
                          variant="body1"
                        >
                          Unit {request.unit.unit_number}
                        </Typography> : null}
                      <Typography
                        sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary', lineHeight: 1.4 }}
                        variant="body1"
                      >
                        {request.unit.address}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.3 }} variant="body2">
                        {[
                          request.unit.city,
                          request.unit.state,
                          request.unit.zip_code
                        ].filter(Boolean).join(', ') || 'Location details not provided'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card> : null}

      {/* Unit Residents Card */}
      {request?.unit_id && request?.unit && canViewUserDetails() ? <Card
          sx={{
            borderRadius: 3,
            boxShadow: theme.shadows[2],
            border:
              theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            backgroundColor: theme.palette.background.paper,
            overflow: 'hidden',
          }}
        >
          <CardHeader
            avatar={
              <Avatar
                sx={{
                  backgroundColor: 'primary.main',
                  width: 48,
                  height: 48,
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                }}
              >
                <UsersIcon size={24} />
              </Avatar>
            }
            subtitle={
              <Typography color="text.secondary" variant="body2">
                People currently living in this unit
              </Typography>
            }
            sx={{
              pb: 2,
              background:
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)'
                  : 'linear-gradient(135deg, rgba(99, 102, 241, 0.02) 0%, rgba(255, 255, 255, 0.8) 100%)',
              borderBottom:
                theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
            }}
            title={
              <Typography sx={{ fontWeight: 700, color: 'text.primary' }} variant="h6">
                Unit Residents
              </Typography>
            }
          />

          <CardContent sx={{ p: 3 }}>
            {request.unit.current_residents && request.unit.current_residents.length > 0 ? (
              <Grid container spacing={2}>
                {request.unit.current_residents.map((resident: any, index: number) => (
                  <Grid key={resident.id || index} md={4} sm={6} xs={12}>
                    <Link href={paths.users.details(resident.id.toString())} underline="none">
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          backgroundColor:
                            theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.04)',
                          border:
                            theme.palette.mode === 'dark'
                              ? '1px solid rgba(99, 102, 241, 0.2)'
                              : '1px solid rgba(99, 102, 241, 0.15)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor:
                              theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.08)',
                            transform: 'translateY(-2px)',
                            boxShadow: theme.shadows[4],
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center' }}>
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              backgroundColor: 'primary.main',
                              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                              fontSize: '1.25rem',
                              fontWeight: 600,
                            }}
                          >
                            {resident.first_name && resident.last_name ? (
                              `${resident.first_name[0]}${resident.last_name[0]}`
                            ) : (
                              <UserIcon size={24} />
                            )}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              variant="subtitle1"
                            >
                              {resident.first_name && resident.last_name
                                ? `${resident.first_name} ${resident.last_name}`
                                : resident.email || 'Unknown Resident'}
                            </Typography>
                            {resident.email ? <Typography
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.875rem',
                                }}
                                variant="body2"
                              >
                                {resident.email}
                              </Typography> : null}
                          </Box>
                        </Stack>
                      </Paper>
                    </Link>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                  borderRadius: 2,
                  border:
                    theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.05)'
                      : '1px solid rgba(0, 0, 0, 0.05)',
                }}
              >
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    color: 'text.secondary',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <UserIcon size={32} />
                </Avatar>
                <Typography color="text.secondary" sx={{ mb: 1, fontWeight: 600 }} variant="h6">
                  No Residents
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  This unit currently has no assigned residents
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card> : null}
    </Stack>
  );
}
